<template>
  <div class="ts-page">
    <div class="ts-page-header">
      <div class="ts-page-title">Stato Server Allianz</div>
      <div class="ts-page-subtitle">
        Monitoraggio heartbeat in tempo reale.
      </div>
    </div>

    <div class="ts-panel">
      <div class="ts-card">
        <div class="ts-card-header">
          <div class="ts-card-title">Connessione server</div>
          <div class="ts-card-header-right">
            <span v-if="lastUpdated" class="ts-timing-text">
              Aggiornato {{ secondsSinceUpdateLabel }} · Prossimo tra {{ countdown }}s
            </span>
            <button
              class="p-button p-button-info ts-btn ts-btn-small ts-btn-ghost"
              type="button"
              :disabled="loading"
              @click="manualRefresh"
            >
              <span class="pi" :class="loading ? 'pi-spin pi-spinner' : 'pi-refresh'" style="margin-right:5px;"></span>
              {{ loading ? 'Aggiornamento...' : 'Aggiorna' }}
            </button>
          </div>
        </div>

        <div class="ts-card-body">
          <!-- Errore -->
          <div v-if="error" class="ts-error-banner">
            <span class="pi pi-exclamation-triangle" style="flex-shrink:0; font-size:16px;"></span>
            <span>Impossibile recuperare lo stato del server.</span>
            <button
              class="p-button p-button-info ts-btn ts-btn-small ts-btn-ghost"
              type="button"
              :disabled="loading"
              @click="manualRefresh"
              style="margin-left:auto;"
            >Riprova</button>
          </div>

          <!-- Prima load -->
          <div v-if="loading && !data" class="ts-first-load">
            <Loader />
            <span>Recupero stato in corso...</span>
          </div>

          <!-- Contenuto principale -->
          <div v-if="data" class="ts-main-layout">

            <!-- Colonna sinistra: stato -->
            <div class="ts-status-col" :class="statusBgClass">
              <div class="ts-status-icon-wrap">
                <span class="pi ts-status-icon" :class="[statusIconClass, statusColorClass]"></span>
              </div>
              <div class="ts-status-label" :class="statusColorClass">{{ statusLabel }}</div>
              <div class="ts-status-sub">{{ minutesAgoLabel }}</div>

              <span v-if="data.consecutive_misses > 0" class="ts-miss-badge">
                <span class="pi pi-exclamation-triangle" style="margin-right:5px;"></span>
                {{ data.consecutive_misses }} miss{{ data.consecutive_misses > 1 ? 'es' : '' }} consecutiv{{ data.consecutive_misses > 1 ? 'i' : 'o' }}
              </span>
            </div>

            <!-- Colonna destra: dettagli -->
            <div class="ts-details-col">
              <div class="ts-detail-card">
                <div class="ts-detail-label">Ultimo heartbeat</div>
                <div class="ts-detail-value">{{ formatDateTime(data.last_heartbeat_at) }}</div>
              </div>
              <div class="ts-detail-card">
                <div class="ts-detail-label">Da quanto tempo</div>
                <div class="ts-detail-value">{{ minutesAgoLabel }}</div>
              </div>
              <div class="ts-detail-card">
                <div class="ts-detail-label">Soglia offline</div>
                <div class="ts-detail-value">{{ data.threshold_minutes }} minuti</div>
              </div>
              <div class="ts-detail-card" :class="{ 'ts-detail-card-warn': data.consecutive_misses > 0 }">
                <div class="ts-detail-label">Misses consecutivi</div>
                <div class="ts-detail-value" :class="{ 'ts-value-warn': data.consecutive_misses > 0 }">
                  {{ data.consecutive_misses }}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '@/components/Loader.vue'
import AllianzService from '@/services/allianz/allianz-service.js'

const POLL_SECONDS = 30

export default {
  name: 'AllianzServerStatus',
  components: { Loader },

  data() {
    return {
      service: null,
      data: null,
      loading: false,
      error: false,
      lastUpdated: null,
      countdown: POLL_SECONDS,
      secondsSinceUpdate: null,
      tickInterval: null,
    }
  },

  computed: {
    statusBgClass() {
      if (!this.data) return ''
      return { online: 'ts-bg-online', offline: 'ts-bg-offline', never_seen: 'ts-bg-unknown' }[this.data.status] || 'ts-bg-unknown'
    },

    statusIconClass() {
      if (!this.data) return ''
      return { online: 'pi-check-circle', offline: 'pi-times-circle', never_seen: 'pi-question-circle' }[this.data.status] || 'pi-question-circle'
    },

    statusColorClass() {
      if (!this.data) return ''
      return { online: 'ts-color-online', offline: 'ts-color-offline', never_seen: 'ts-color-unknown' }[this.data.status] || 'ts-color-unknown'
    },

    statusLabel() {
      if (!this.data) return ''
      return { online: 'Server Online', offline: 'Server Offline', never_seen: 'Nessun dato ricevuto' }[this.data.status] || 'Stato sconosciuto'
    },

    minutesAgoLabel() {
      const m = this.data?.minutes_ago
      if (m === null || m === undefined) return '-'
      if (m === 0) return 'Adesso'
      if (m === 1) return '1 minuto fa'
      return `${m} minuti fa`
    },

    secondsSinceUpdateLabel() {
      const s = this.secondsSinceUpdate
      if (s === null) return '...'
      if (s < 5) return 'adesso'
      if (s < 60) return `${s}s fa`
      const m = Math.floor(s / 60)
      return m === 1 ? '1 min fa' : `${m} min fa`
    },
  },

  created() {
    this.service = new AllianzService(this.$http, this.$eventBus)
  },

  async mounted() {
    await this.fetchStatus()
    this.tickInterval = setInterval(this.tick, 1000)
  },

  beforeUnmount() {
    clearInterval(this.tickInterval)
  },

  methods: {
    tick() {
      if (this.secondsSinceUpdate !== null) this.secondsSinceUpdate++
      if (this.loading) return
      this.countdown--
      if (this.countdown <= 0) this.fetchStatus()
    },

    manualRefresh() {
      this.countdown = POLL_SECONDS
      this.fetchStatus()
    },

    async fetchStatus() {
      if (this.loading) return
      this.loading = true
      this.error = false
      try {
        const response = await this.service.getStatus()
        if (!response?.ok) {
          this.error = true
          this.data = null
        } else {
          this.data = response
          this.lastUpdated = true
          this.secondsSinceUpdate = 0
        }
      } catch (err) {
        console.error('Errore stato server Allianz:', err)
        this.error = true
        this.data = null
      } finally {
        this.loading = false
        this.countdown = POLL_SECONDS
      }
    },

    formatDateTime(value) {
      if (!value) return '-'
      const s = String(value).trim()
      if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(s)) {
        const [datePart, timePart] = s.split(' ')
        const [year, month, day] = datePart.split('-')
        return `${day}/${month}/${year} ${timePart}`
      }
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString('it-IT')
    },
  },
}
</script>

<style scoped>
.ts-page { padding: 18px !important; }
.ts-page-header { margin-bottom: 18px !important; }
.ts-page-title { font-size: 22px !important; font-weight: 800 !important; margin: 0 !important; }
.ts-page-subtitle { margin-top: 6px !important; font-size: 13px !important; opacity: 0.9 !important; }

.ts-btn { border-radius: 10px !important; }
.ts-btn-small { padding: 0.45rem 0.75rem !important; font-size: 0.85rem !important; }
.ts-btn-ghost { background: transparent !important; color: #0b66a7 !important; border: 1px solid rgba(6, 141, 224, 0.55) !important; }

.ts-panel { display: grid !important; gap: 14px !important; }

.ts-card {
  background: #ffffff !important;
  border: 2px solid rgba(17, 24, 39, 0.18) !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.10) !important;
}

.ts-card-header {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 16px 20px !important;
  border-bottom: 2px solid rgba(17, 24, 39, 0.12) !important;
  background: rgba(6, 141, 224, 0.10) !important;
  gap: 12px !important;
  flex-wrap: wrap !important;
}

.ts-card-title { font-size: 16px !important; font-weight: 800 !important; }

.ts-card-header-right {
  display: flex !important;
  align-items: center !important;
  gap: 16px !important;
  flex-wrap: wrap !important;
}

.ts-timing-text { font-size: 12px !important; color: #6b7280 !important; }

.ts-card-body { padding: 24px !important; }

.ts-error-banner {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  background: rgba(239, 68, 68, 0.08) !important;
  border: 1.5px solid rgba(239, 68, 68, 0.30) !important;
  border-radius: 8px !important;
  padding: 14px 18px !important;
  color: #b91c1c !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  margin-bottom: 20px !important;
}

.ts-first-load {
  display: flex !important;
  align-items: center !important;
  gap: 12px !important;
  padding: 40px 0 !important;
  justify-content: center !important;
  font-size: 13px !important;
  color: #6b7280 !important;
}

/* Layout principale: sinistra status, destra dettagli */
.ts-main-layout {
  display: grid !important;
  grid-template-columns: 280px 1fr !important;
  gap: 24px !important;
  align-items: stretch !important;
}

/* Colonna stato */
.ts-status-col {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 12px !important;
  padding: 40px 24px !important;
  border-radius: 10px !important;
  border: 1.5px solid !important;
  text-align: center !important;
}

.ts-bg-online  { background: rgba(34, 197, 94, 0.07) !important;  border-color: rgba(34, 197, 94, 0.30) !important; }
.ts-bg-offline { background: rgba(239, 68, 68, 0.07) !important;  border-color: rgba(239, 68, 68, 0.30) !important; }
.ts-bg-unknown { background: rgba(17, 24, 39, 0.03) !important;   border-color: rgba(17, 24, 39, 0.14) !important;  }

.ts-status-icon-wrap { margin-bottom: 4px !important; }
.ts-status-icon { font-size: 48px !important; }

.ts-color-online  { color: #15803d !important; }
.ts-color-offline { color: #b91c1c !important; }
.ts-color-unknown { color: #6b7280 !important; }

.ts-status-label { font-size: 20px !important; font-weight: 900 !important; }

.ts-status-sub { font-size: 13px !important; color: #6b7280 !important; }

.ts-miss-badge {
  display: inline-flex !important;
  align-items: center !important;
  margin-top: 8px !important;
  padding: 5px 12px !important;
  border-radius: 8px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  background: rgba(245, 158, 11, 0.10) !important;
  border: 1.5px solid rgba(245, 158, 11, 0.35) !important;
  color: #b45309 !important;
}

/* Colonna dettagli */
.ts-details-col {
  display: grid !important;
  grid-template-columns: repeat(2, 1fr) !important;
  gap: 14px !important;
  align-content: start !important;
}

.ts-detail-card {
  background: #f8fbfe !important;
  border: 1px solid rgba(17, 24, 39, 0.10) !important;
  border-radius: 10px !important;
  padding: 20px !important;
}

.ts-detail-card-warn {
  background: rgba(245, 158, 11, 0.06) !important;
  border-color: rgba(245, 158, 11, 0.25) !important;
}

.ts-detail-label {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #9ca3af !important;
  margin-bottom: 8px !important;
}

.ts-detail-value { font-size: 16px !important; font-weight: 700 !important; color: #111827 !important; }
.ts-value-warn { color: #b45309 !important; }

@media (max-width: 1024px) {
  .ts-main-layout { grid-template-columns: 1fr !important; }
  .ts-status-col { padding: 28px !important; }
  .ts-details-col { grid-template-columns: repeat(2, 1fr) !important; }
}

@media (max-width: 560px) {
  .ts-details-col { grid-template-columns: 1fr !important; }
}
</style>
